/**
 * Localized label for a sync job's data source.
 *
 * `sync-monitoring.service.ts` fills a data source that has no name with the
 * ENGLISH sentinel `'Unknown Source'` (three call sites, all `?? 'Unknown
 * Source'`), so `sourceName` is never null — it is either a real name or that
 * literal. Rendering it raw leaks English into the Spanish admin UI.
 *
 * This lived as a private helper inside `DashboardMonitoringWidgets`, which is
 * why the sync MONITOR page kept printing the sentinel: it renders the same
 * field from the same endpoint and had no access to the resolver. One
 * implementation, both callers — a second copy would drift the moment the
 * backend sentinel changes.
 *
 * The empty-string case is handled too: a source named `'   '` is just as
 * unnamed as one the backend defaulted, and trimming keeps the two consistent.
 */

/** What the label needs of a `t`: both the platform's and react-i18next's satisfy it. */
type TranslateLabel = (key: string, defaultValue: string) => string

/** The literal the backend substitutes for a missing data-source name. */
export const BACKEND_MISSING_SOURCE_SENTINEL = 'Unknown Source'

export const resolveSyncSourceLabel = (
  sourceName: string | null | undefined,
  t: TranslateLabel,
): string => {
  const trimmed = (sourceName ?? '').trim()
  return trimmed === '' || trimmed === BACKEND_MISSING_SOURCE_SENTINEL
    ? t('monitoring.unknownSource', 'Unknown source')
    : trimmed
}
